// 模型展示页面 - Tailwind CSS 现代简约风格
import React, { useState, useEffect } from 'react';
import { Layers, Code, DollarSign, Tag, Search, Zap, BookOpen, Copy, Check, TrendingUp, BarChart3 } from 'lucide-react';
import { api } from '../lib/api';
import Navbar from '../components/Navbar';
import ModelTesting from '../components/ModelTesting';
import AnimatedNumber from '../components/AnimatedNumber';
import LoadingSpinner from '../components/LoadingSpinner';

interface Model {
  id: number;
  name: string;
  slug: string;
  provider_model_name: string;
  description: string;
  status: string;
  parameters: any;
  tags: string[];
  example_template?: string;
  pricing: {
    display_price: string;
    pricing_type: string;
    description: string;
  };
}

interface Category {
  id: number;
  name: string;
  slug: string;
  description: string;
  api_prefix: string;
  models: Model[];
}

interface UserStats {
  balance: number;
  statistics: {
    total_calls: number;
    total_spent: number;
    monthly_spent: number;
    monthly_calls: number;
  };
}

const Models: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedModel, setSelectedModel] = useState<Model | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [selectedModelForModal, setSelectedModelForModal] = useState<Model | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTab, setModalTab] = useState<'details' | 'tryit'>('details');
  const [userBalance, setUserBalance] = useState<number | null>(null);
  const [userStats, setUserStats] = useState<UserStats | null>(null);



  useEffect(() => {
    fetchModels();
    fetchUserStats();
  }, []);

  // 控制弹窗时的body滚动
  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // 清理函数，组件卸载时恢复滚动
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen]);

  const fetchModels = async () => {
    try {
      const response = await api.get('/v1/models');
      const data = response.data.data;
      setCategories(data);
      if (data.length > 0) {
        setSelectedCategory(data[0].slug);
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to load models');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserBalance = async () => {
    try {
      const response = await api.get('/api/user/info');
      setUserBalance(response.data.balance);
      return response.data.balance;
    } catch (err: any) {
      // Failed to fetch user balance
      return null;
    }
  };

  const fetchUserStats = async () => {
    try {
      const response = await api.get('/api/user/info');
      setUserStats({
        balance: response.data.balance,
        statistics: response.data.statistics
      });
    } catch (err: any) {
      // Failed to fetch user stats
    }
  };

  const openModal = (model: Model) => {
    setSelectedModelForModal(model);
    setIsModalOpen(true);
    if (modalTab === 'tryit') {
      fetchUserBalance();
    }
  };

  const handleBalanceUpdate = (newBalance: number) => {
    setUserBalance(newBalance);
  };

  const getCurrentCategory = () => {
    return categories.find(cat => cat.slug === selectedCategory);
  };

  const getFilteredModels = () => {
    const currentCategory = getCurrentCategory();

    // 如果有搜索词，搜索所有分类中的模型
    if (searchTerm) {
      const allModels: Model[] = [];
      categories.forEach(category => {
        category.models.forEach(model => {
          if (
            model.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            model.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
            model.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
          ) {
            allModels.push(model);
          }
        });
      });
      return allModels;
    }

    // 如果没有搜索词，返回当前分类的模型
    if (!currentCategory) return [];
    return currentCategory.models;
  };

  const getExampleCode = (model: Model, category: Category) => {
    const apiKey = '****************************************';
    const baseUrl = 'https://p.qmtju.com';

    // 如果模型有自定义模板，使用模板并替换变量
    if (model.example_template) {
      return model.example_template
        .replace(/\{\{api_key\}\}/g, apiKey)
        .replace(/\{\{base_url\}\}/g, baseUrl)
        .replace(/\{\{model_slug\}\}/g, model.slug)
        .replace(/\{\{model_name\}\}/g, model.name);
    }

    // 回退到默认的分类模板
    const endpoint = category.api_prefix;

    if (category.slug === 'text-generation') {
      return `curl -X POST "${baseUrl}${endpoint}/chat/completions" \\
  -H "x-api-key: ${apiKey}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "${model.slug}",
    "messages": [
      {"role": "user", "content": "Hello!"}
    ],
    "max_tokens": 100
  }'`;
    } else if (category.slug === 'image-generation') {
      return `curl -X POST "${baseUrl}${endpoint}/generations" \\
  -H "x-api-key: ${apiKey}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "${model.slug}",
    "prompt": "A beautiful sunset over mountains",
    "size": "1024x1024"
  }'`;
    } else {
      return `curl -X POST "${baseUrl}${endpoint}" \\
  -H "x-api-key: ${apiKey}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "${model.slug}",
    "input": "Your input here"
  }'`;
    }
  };

  // 关闭弹窗的函数
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedModelForModal(null);
    setModalTab('details'); // 重置到详情标签页
  };

  const copyToClipboard = async (text: string, modelId: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCode(modelId.toString());
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      alert('Failed to copy code');
    }
  };

  const getCategoryIcon = (slug: string) => {
    const icons: { [key: string]: string } = {
      'text-generation': '💬',
      'image-generation': '🖼️',
      'audio-generation': '🔊',
      'video-generation': '🎬',
      'speech-recognition': '🎤',
      'embeddings': '🔗',
      'code-generation': '💻',
      'vision': '👁️',
      '3d-generation': '🗿',
      'safety': '🛡️',
      'industry': '🏭'
    };
    return icons[slug] || '🤖';
  };

  const getTagColor = (tag: string) => {
    const colors: { [key: string]: string } = {
      'text': 'bg-blue-100 text-blue-800',
      'image': 'bg-purple-100 text-purple-800',
      'audio': 'bg-green-100 text-green-800',
      'video': 'bg-red-100 text-red-800',
      'async': 'bg-orange-100 text-orange-800',
      'sync': 'bg-gray-100 text-gray-800',
      'generation': 'bg-indigo-100 text-indigo-800',
      'chat': 'bg-cyan-100 text-cyan-800'
    };
    return colors[tag] || 'bg-gray-100 text-gray-600';
  };

  if (loading) {
    return (
      <div>
        <Navbar />
        <LoadingSpinner size="medium" text="Loading AI models..." fullPage={true} />
      </div>
    );
  }

  const currentCategory = getCurrentCategory();
  const filteredModels = getFilteredModels();

  return (
    <>
      <Navbar />
      <div className="bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 relative">

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg mb-6">
              <span>{error}</span>
            </div>
          )}

          {/* 左侧分类导航 - 固定定位 */}
          <div className="fixed top-20 left-1/2 transform -translate-x-1/2 ml-[-448px] w-64 z-10 space-y-3">
            {/* 分类导航 */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 h-[28rem]">
              <nav className="space-y-0.5 h-full overflow-y-auto overflow-x-hidden pl-1 pr-3" style={{
                scrollbarWidth: 'thin',
                scrollbarColor: '#cbd5e1 transparent'
              }}>
                {categories.map((category) => (
                  <button
                    key={category.slug}
                    onClick={() => setSelectedCategory(category.slug)}
                    className={`w-full text-left px-2.5 py-1.5 rounded-md transition-all duration-300 ease-out relative ${selectedCategory === category.slug
                        ? 'bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 border-l-4 border-blue-500 transform scale-105 shadow-md'
                        : 'text-gray-700 hover:bg-gray-50 hover:transform hover:scale-102'
                      }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium break-words flex-1 mr-1.5">{category.name}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${selectedCategory === category.slug
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-gray-100 text-gray-600'
                        }`}>
                        {category.models.length}
                      </span>
                    </div>
                  </button>
                ))}
              </nav>
            </div>
            
            {/* 搜索框 */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search models..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-8 pr-8 py-2 border border-gray-200 rounded-md text-xs bg-gray-50 placeholder-gray-500 focus:outline-none focus:bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              />
            </div>

            {/* 用户统计卡片 */}
            {userStats && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
                <h3 className="text-xs font-semibold text-gray-700 mb-2">Account Overview</h3>
                <div className="space-y-2">
                  {/* Account Balance */}
                  <div className="flex items-center justify-between p-2 bg-green-50 rounded-md">
                    <div className="flex items-center space-x-2">
                      <TrendingUp className="h-3 w-3 text-green-600" />
                      <span className="text-xs text-gray-600">Balance</span>
                    </div>
                    <span className="text-sm font-bold text-green-600">
                      ${userStats.balance.toFixed(2)}
                    </span>
                  </div>
                  
                  {/* Total API Calls */}
                  <div className="flex items-center justify-between p-2 bg-blue-50 rounded-md">
                    <div className="flex items-center space-x-2">
                      <BarChart3 className="h-3 w-3 text-blue-600" />
                      <span className="text-xs text-gray-600">Total Calls</span>
                    </div>
                    <span className="text-sm font-bold text-gray-900">
                      {userStats.statistics.total_calls?.toLocaleString() || '0'}
                    </span>
                  </div>
                  
                  {/* This Month */}
                  <div className="flex items-center justify-between p-2 bg-purple-50 rounded-md">
                    <div className="flex items-center space-x-2">
                      <DollarSign className="h-3 w-3 text-purple-600" />
                      <span className="text-xs text-gray-600">This Month</span>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold text-gray-900">
                        ${userStats.statistics.monthly_spent?.toFixed(2) || '0.00'}
                      </div>
                      <div className="text-xs text-purple-600">
                        {userStats.statistics.monthly_calls || 0} calls
                      </div>
                    </div>
                  </div>
                </div>
              </div>
             )}
          </div>
          


          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* 左侧占位空间 */}
            <div className="lg:col-span-1"></div>

            {/* 右侧模型内容 */}
            <div className="lg:col-span-3">
              {currentCategory && (
                <div>
                  {/* Search results statistics */}
                  {searchTerm && (
                    <div className="mb-6 text-sm text-gray-600">
                      Found <span className="font-medium text-gray-900">{filteredModels.length}</span> model{filteredModels.length !== 1 ? 's' : ''}
                      <span className="ml-2">
                        containing "<span className="font-medium text-blue-600">{searchTerm}</span>"
                      </span>
                    </div>
                  )}

                  {/* 模型列表 */}
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {filteredModels.map((model) => (
                      <div
                        key={model.id}
                        className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:shadow-md transition-all duration-200 cursor-pointer hover:-translate-y-1 min-h-56 flex flex-col"
                        onClick={() => openModal(model)}
                      >
                        <h3 className="text-sm font-semibold text-gray-900 mb-2 line-clamp-2">{model.name}</h3>
                        <div className="text-xs font-medium text-green-600 mb-3">
                          {model.pricing?.display_price || 'Contact for pricing'}
                        </div>
                        <div className="flex items-center justify-between mb-3">
                          <code className="text-xs font-mono text-gray-600 bg-gray-100 px-2 py-1 rounded flex-1 mr-2 truncate">
                            {model.slug}
                          </code>
                          <div className="relative">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                copyToClipboard(model.slug, model.id);
                              }}
                              className="p-1 text-gray-400 hover:text-blue-600 transition duration-200"
                              title={copiedCode === model.id.toString() ? 'Copied!' : 'Copy API name'}
                            >
                              {copiedCode === model.id.toString() ? (
                                <Check className="h-4 w-4 text-green-600" />
                              ) : (
                                <Copy className="h-4 w-4" />
                              )}
                            </button>
                            {copiedCode === model.id.toString() && (
                              <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-1 bg-gray-900 text-white text-xs rounded-lg">
                                Copied!
                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-2 border-r-2 border-b-2 border-transparent border-b-gray-900"></div>
                              </div>
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-gray-600 line-clamp-6 flex-1">{model.description}</p>
                      </div>
                    ))}

                    {filteredModels.length === 0 && searchTerm && (
                      <div className="col-span-full flex flex-col items-center justify-center py-12">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                          <Search className="h-8 w-8 text-gray-400" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No models found</h3>
                        <p className="text-gray-600 text-center max-w-md">
                          Try adjusting your search terms or browse different categories
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 模型详情弹窗 */}
      {isModalOpen && selectedModelForModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-2" onClick={closeModal}>
          <div className="bg-white rounded-lg shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="border-b border-gray-200">
              {/* 标签页导航 */}
              <div className="flex items-center justify-between px-3 pt-3">
                <div className="flex items-center space-x-4">
                  <div className="flex space-x-1">
                    <button
                      onClick={() => setModalTab('details')}
                      className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${modalTab === 'details'
                          ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-700'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                        }`}
                    >
                      Details
                    </button>
                    <button
                      onClick={() => {
                        setModalTab('tryit');
                        fetchUserBalance();
                      }}
                      className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${modalTab === 'tryit'
                          ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-700'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                        }`}
                    >
                      Try It
                    </button>
                  </div>

                  {/* 模型信息和余额显示 - 只在Try It标签页显示 */}
                  {modalTab === 'tryit' && (
                    <div className="flex items-center space-x-4 text-sm">
                      <div className="flex items-center space-x-2">
                        <span className="font-medium text-gray-900">{selectedModelForModal.name}</span>
                        <span className="text-gray-400">•</span>
                        <span className="text-emerald-600 font-semibold">Balance: ${userBalance !== null ? <AnimatedNumber value={userBalance} decimals={2} /> : '...'}</span>
                        <span className="text-gray-400">•</span>
                        <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full">
                          {selectedModelForModal.pricing?.display_price || 'Contact for pricing'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
                <button
                  className="text-gray-400 hover:text-gray-600 text-sm font-medium"
                  onClick={closeModal}
                >
                  Close
                </button>
              </div>
            </div>
            <div className="h-[80vh] overflow-y-auto p-6">
              {modalTab === 'details' && (
                <>
                  {/* 模型基本信息 */}
                  <div className="mb-3">
                    <h3 className="text-xs font-medium text-gray-900 mb-2">Basic Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <div className="bg-gray-50 p-2 rounded">
                        <span className="text-xs font-medium text-gray-600">Model Name:</span>
                        <div className="text-xs text-gray-900 mt-1">{selectedModelForModal.name}</div>
                      </div>
                      <div className="bg-gray-50 p-2 rounded">
                        <span className="text-xs font-medium text-gray-600">API Name:</span>
                        <div className="text-xs font-mono text-gray-900 mt-1">{selectedModelForModal.slug}</div>
                      </div>
                      <div className="bg-gray-50 p-2 rounded md:col-span-2">
                        <span className="text-xs font-medium text-gray-600">Pricing:</span>
                        <div className="text-xs font-medium text-green-600 mt-1">{selectedModelForModal.pricing?.display_price || 'Contact for pricing'}</div>
                      </div>
                    </div>
                  </div>

                  {/* 模型描述 */}
                  <div className="mb-3">
                    <h3 className="text-xs font-medium text-gray-900 mb-2">Description</h3>
                    <p className="text-sm text-gray-700 leading-relaxed">{selectedModelForModal.description}</p>
                  </div>

                  {/* 模型标签 */}
                  {selectedModelForModal.tags && selectedModelForModal.tags.length > 0 && (
                    <div className="mb-3">
                      <h3 className="text-xs font-medium text-gray-900 mb-2">Tags</h3>
                      <div className="flex flex-wrap gap-1">
                        {selectedModelForModal.tags.map((tag, index) => (
                          <span key={index} className={`px-2 py-1 rounded-full text-xs font-medium ${getTagColor(tag)}`}>
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* API示例代码 */}
                  <div>
                    <h3 className="text-xs font-medium text-gray-900 mb-2">API Example</h3>
                    <div className="bg-gray-50 rounded border overflow-hidden">
                      <div className="bg-gray-100 px-3 py-2 border-b flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <Code className="h-3 w-3 text-gray-600" />
                          <span className="text-xs font-medium text-gray-700">cURL Example</span>
                        </div>
                        <button
                          className="flex items-center space-x-1 px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition duration-200"
                          onClick={() => copyToClipboard(getExampleCode(selectedModelForModal, currentCategory!), selectedModelForModal.id)}
                          title="Copy code"
                        >
                          {copiedCode === selectedModelForModal.id.toString() ? (
                            <Check className="h-3 w-3" />
                          ) : (
                            <Copy className="h-3 w-3" />
                          )}
                          <span className="text-xs font-medium">Copy</span>
                        </button>
                      </div>
                      <pre className="p-3 overflow-x-auto">
                        <code className="text-xs text-gray-800 whitespace-pre-wrap">
                          {getExampleCode(selectedModelForModal, currentCategory!)}
                        </code>
                      </pre>
                    </div>
                  </div>
                </>
              )}

              {modalTab === 'tryit' && (
                <ModelTesting
                  model={selectedModelForModal}
                  onBalanceUpdate={handleBalanceUpdate}
                />
              )}
            </div>
          </div>
        </div>
      )}


    </>
  );
};

export default Models;